(() => {
  const elementName = 'trustpilot-ranking';

  if (customElements.get(elementName)) return;

  class TrustpilotRanking extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.trigger = this.querySelector('.pdp-trustpilot-ranking__trigger');
      this.disclosure = this.querySelector('.pdp-trustpilot-ranking__disclosure');

      if (!this.trigger || !this.disclosure) return;

      this.dataset.initialized = 'true';
      this.isPinned = false;
      this.closeTimer = null;
      this.hasNativePopover = typeof this.disclosure.showPopover === 'function';

      this.onTriggerPointerEnter = this.handleTriggerPointerEnter.bind(this);
      this.onTriggerPointerLeave = this.handleTriggerPointerLeave.bind(this);
      this.onDisclosurePointerEnter = this.cancelScheduledClose.bind(this);
      this.onDisclosurePointerLeave = this.scheduleClose.bind(this);
      this.onTriggerFocus = this.handleTriggerFocus.bind(this);
      this.onTriggerBlur = this.scheduleClose.bind(this);
      this.onTriggerClick = this.handleTriggerClick.bind(this);
      this.onTriggerKeydown = this.handleTriggerKeydown.bind(this);
      this.onDisclosureClick = this.handleDisclosureClick.bind(this);
      this.onDisclosureToggle = this.handleDisclosureToggle.bind(this);
      this.onDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
      this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.onViewportChange = this.handleViewportChange.bind(this);

      this.trigger.addEventListener('pointerenter', this.onTriggerPointerEnter);
      this.trigger.addEventListener('pointerleave', this.onTriggerPointerLeave);
      this.trigger.addEventListener('focus', this.onTriggerFocus);
      this.trigger.addEventListener('blur', this.onTriggerBlur);
      this.trigger.addEventListener('click', this.onTriggerClick);
      this.trigger.addEventListener('keydown', this.onTriggerKeydown);
      this.disclosure.addEventListener('pointerenter', this.onDisclosurePointerEnter);
      this.disclosure.addEventListener('pointerleave', this.onDisclosurePointerLeave);
      this.disclosure.addEventListener('click', this.onDisclosureClick);
      this.disclosure.addEventListener('toggle', this.onDisclosureToggle);
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
      document.addEventListener('keydown', this.onDocumentKeydown);
      window.addEventListener('resize', this.onViewportChange);
      window.addEventListener('scroll', this.onViewportChange, true);
    }

    disconnectedCallback() {
      if (this.dataset.initialized !== 'true') return;

      this.cancelScheduledClose();
      this.trigger?.removeEventListener('pointerenter', this.onTriggerPointerEnter);
      this.trigger?.removeEventListener('pointerleave', this.onTriggerPointerLeave);
      this.trigger?.removeEventListener('focus', this.onTriggerFocus);
      this.trigger?.removeEventListener('blur', this.onTriggerBlur);
      this.trigger?.removeEventListener('click', this.onTriggerClick);
      this.trigger?.removeEventListener('keydown', this.onTriggerKeydown);
      this.disclosure?.removeEventListener('pointerenter', this.onDisclosurePointerEnter);
      this.disclosure?.removeEventListener('pointerleave', this.onDisclosurePointerLeave);
      this.disclosure?.removeEventListener('click', this.onDisclosureClick);
      this.disclosure?.removeEventListener('toggle', this.onDisclosureToggle);
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      window.removeEventListener('resize', this.onViewportChange);
      window.removeEventListener('scroll', this.onViewportChange, true);
      delete this.dataset.initialized;
    }

    handleTriggerPointerEnter(event) {
      if (event.pointerType === 'touch') return;

      this.open(false);
    }

    handleTriggerPointerLeave(event) {
      if (event.pointerType === 'touch') return;

      this.scheduleClose();
    }

    handleTriggerFocus() {
      this.open(this.isPinned);
    }

    handleTriggerClick(event) {
      event.preventDefault();
      event.stopPropagation();

      if (this.isOpen() && this.isPinned) {
        this.close();
        return;
      }

      this.open(true);
    }

    handleTriggerKeydown(event) {
      if (![' ', 'Space', 'Spacebar'].includes(event.key)) return;

      event.preventDefault();
      event.stopPropagation();

      if (this.isOpen() && this.isPinned) {
        this.close();
        return;
      }

      this.open(true);
    }

    handleDisclosureClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.close();
    }

    handleDisclosureToggle(event) {
      const isOpen = event.newState ? event.newState === 'open' : this.isOpen();
      this.trigger.setAttribute('aria-expanded', String(isOpen));

      if (!isOpen) this.isPinned = false;
    }

    handleDocumentPointerDown(event) {
      if (!this.isOpen() || this.contains(event.target)) return;

      this.close();
    }

    handleDocumentKeydown(event) {
      if (event.key === 'Escape' && this.isOpen()) this.close();
    }

    handleViewportChange() {
      if (this.isOpen()) this.positionDisclosure();
    }

    isOpen() {
      if (this.hasNativePopover) return this.disclosure.matches(':popover-open');

      return this.disclosure.classList.contains('is-open');
    }

    open(pinDisclosure) {
      this.cancelScheduledClose();
      this.isPinned = pinDisclosure;

      if (this.hasNativePopover) {
        if (!this.isOpen()) {
          this.disclosure.style.visibility = 'hidden';
          this.disclosure.showPopover();
        }
      } else {
        this.disclosure.classList.add('is-open');
      }

      this.positionDisclosure();
      this.disclosure.style.removeProperty('visibility');
      this.trigger.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.cancelScheduledClose();
      this.isPinned = false;

      if (this.hasNativePopover) {
        if (this.isOpen()) this.disclosure.hidePopover();
      } else {
        this.disclosure.classList.remove('is-open');
      }

      this.trigger.setAttribute('aria-expanded', 'false');
    }

    scheduleClose() {
      this.cancelScheduledClose();
      this.closeTimer = window.setTimeout(() => {
        const triggerHasFocus = document.activeElement === this.trigger;
        const pointerIsWithin = this.trigger.matches(':hover') || this.disclosure.matches(':hover');

        if (!this.isPinned && !triggerHasFocus && !pointerIsWithin) this.close();
      }, 120);
    }

    cancelScheduledClose() {
      if (this.closeTimer === null) return;

      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    positionDisclosure() {
      const viewportPadding = 16;
      const disclosureGap = 8;
      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = document.documentElement.clientHeight;
      const componentRect = this.getBoundingClientRect();
      const triggerRect = this.trigger.getBoundingClientRect();
      const disclosureRect = this.disclosure.getBoundingClientRect();
      const rightSpace = viewportWidth - triggerRect.right - viewportPadding;
      const leftSpace = componentRect.left - viewportPadding;
      let left;
      let top;
      let availableHeight;

      if (rightSpace >= disclosureRect.width + disclosureGap) {
        left = triggerRect.right + disclosureGap;
        top = triggerRect.top;
        availableHeight = viewportHeight - (viewportPadding * 2);
      } else if (leftSpace >= disclosureRect.width + disclosureGap) {
        left = componentRect.left - disclosureGap - disclosureRect.width;
        top = triggerRect.top;
        availableHeight = viewportHeight - (viewportPadding * 2);
      } else {
        const maximumLeft = viewportWidth - disclosureRect.width - viewportPadding;
        const preferredLeft = triggerRect.right - disclosureRect.width;
        left = Math.max(viewportPadding, Math.min(preferredLeft, maximumLeft));
        top = triggerRect.bottom + disclosureGap;
        availableHeight = Math.max(64, viewportHeight - top - viewportPadding);
      }

      top = Math.max(viewportPadding, Math.min(top, viewportHeight - disclosureRect.height - viewportPadding));

      this.disclosure.style.left = `${Math.round(left)}px`;
      this.disclosure.style.top = `${Math.round(top)}px`;
      this.disclosure.style.maxHeight = `${Math.round(availableHeight)}px`;
    }
  }

  customElements.define(elementName, TrustpilotRanking);
})();
